import { LoadingState } from "@repo/app-types/client/client";
import type { SlackIntegrationSettings } from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import type { SlackChannel } from "@repo/app-types/integrations/integrations";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Label } from "@repo/ui/Label/label";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { type FC, useEffect, useState } from "react";
import type { Key } from "react-aria-components";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { openOAuthSigninPopupForIntegration } from "../../../helpers/urlHelpers.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import {
  selectSlackIntegration,
  updateIntegration,
} from "../../integrations/integrationsSlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { OAuthIntegrationButtonsRow } from "./oauthIntegrationCtas.js";

export const fetchSlackChannelsForIntegration = async (
  integrationId: string,
): Promise<SlackChannel[]> => {
  const axios = await getAxiosInstanceWithAuth();
  const { data } = await axios.get(`/slack/${integrationId}/channels`);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  return data.map((channel: any) => ({
    name: `# ${channel.name}`,
    id: channel.id,
  }));
};

export const SlackIntegrationSection: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const slackIntegration = useAppSelector(selectSlackIntegration);

  const [listChannelsLoadingState, setListChannelsLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);
  const [slackChannels, setSlackChannels] = useState<SlackChannel[]>([]);

  const listSlackChannels = async () => {
    if (!slackIntegration) return;

    setListChannelsLoadingState(LoadingState.LOADING);
    try {
      const channels = await fetchSlackChannelsForIntegration(
        slackIntegration.id,
      );

      setSlackChannels(channels);

      setListChannelsLoadingState(LoadingState.LOADED);
    } catch (err) {
      setListChannelsLoadingState(LoadingState.ERROR);
    }
  };

  const onChannelSelect = (channelId: Key | null) => {
    // Clearing the text reports no selection. There was never a way to unset
    // the channel here, so keep the current one rather than saving "none".
    if (!slackIntegration || channelId === null) return;

    dispatch(
      updateIntegration({
        id: slackIntegration.id,
        changes: {
          settings: {
            ...slackIntegration.settings,
            channel_id: String(channelId),
          },
        },
      }),
    );

    showNotification(dispatch, {
      id: `slack-channel-select-${new Date().getTime()}`,
      primaryMessage: "Slack channel updated",
      type: NotificationType.SUCCESS,
    });
  };

  useEffect(() => {
    listSlackChannels();
  }, [slackIntegration]);

  const renderContent = () => {
    if (!slackIntegration)
      return (
        <Can
          I={PermissionActions.CREATE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              onClick={() => openOAuthSigninPopupForIntegration("slack")}
              variant={ButtonVariant.Primary}
              isDisabled={!allowed}
            >
              Login with Slack
            </Button>
          )}
        </Can>
      );

    return (
      <div className="flex flex-col space-y-5">
        <OAuthIntegrationButtonsRow integration={slackIntegration} />

        {listChannelsLoadingState === LoadingState.LOADING ? (
          <DefaultSkeleton />
        ) : (
          <div>
            <div className="w-72">
              <Can
                I={PermissionActions.UPDATE}
                a={PermissionSubjects.INTEGRATIONS}
                passThrough={true}
              >
                {(allowed) => (
                  <ComboboxField<SlackChannel>
                    label="Default Slack channel"
                    items={slackChannels}
                    getLabel={(channel) => channel.name}
                    value={
                      (slackIntegration.settings as SlackIntegrationSettings)
                        .channel_id
                    }
                    onChange={onChannelSelect}
                    placeholder="Search for a public channel..."
                    isDisabled={!allowed}
                  />
                )}
              </Can>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              The default Slack channel to send notifications to when an
              interview is completed. We recommend choosing a popular channel so
              that insights can be shared across your team. You can also
              customize Slack notifications for a particular project from that
              project's settings page.
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="mb-4">
        <Label size={Size.MEDIUM}>Slack</Label>
      </div>
      <div className="mb-4">
        <InfoBanner
          title="How the Slack integration works"
          description={
            <ul className="ml-4 list-disc">
              <li className="mb-2">
                When an interview is completed, a summary of the interview and a
                link to the full transcript will be sent to Slack
              </li>
              <li className="mb-2">
                You can pick a different Slack channel for each project
              </li>
              {company && company.has_sensitive_data ? (
                <li className="mb-2">
                  If you expect interviews to contain sensitive information,
                  ensure that your Slack account is allowed to store that
                  information.
                </li>
              ) : null}
            </ul>
          }
        />
      </div>

      {renderContent()}
    </div>
  );
};
